import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { SafeCryptoLogo } from '@/components/SafeCryptoLogo';
import { ShieldCheck, Gift, Link2, CreditCard, Lock, Users, Globe, Zap } from 'lucide-react';

const slides = [
  { icon: ShieldCheck, color: 'hsl(var(--primary))', key: 'slide1' },
  { icon: Gift, color: '#2DD36F', key: 'slide2' },
  { icon: Link2, color: '#F7C84B', key: 'slide3' },
  { icon: CreditCard, color: '#28AAFF', key: 'slide4' },
];

const trustBadges = [
  { icon: Lock, label: '256-bit SSL' },
  { icon: ShieldCheck, label: '2FA' },
  { icon: Globe, label: 'AML/KYC' },
];

export function AuthSlider() {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[current];

  return (
    <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 overflow-hidden">
      <div className="absolute inset-0" style={{ background: `radial-gradient(700px circle at 30% 50%, ${slide.color}18, transparent 60%)` }} />
      <div className="noise-overlay" />
      <div className="relative w-full max-w-md flex flex-col justify-between h-full py-4">
        {/* Top - Logo */}
        <div className="flex items-center gap-3">
          <SafeCryptoLogo size={44} />
          <span className="text-2xl font-bold font-['Space_Grotesk'] tracking-tight">Safe Crypto</span>
        </div>

        {/* Middle - Slide Content */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="relative min-h-[220px]">
            {slides.map((s, i) => {
              const SlideIcon = s.icon;
              return (
                <div
                  key={s.key}
                  className="absolute inset-0 transition-all duration-700 ease-in-out"
                  style={{
                    opacity: i === current ? 1 : 0,
                    transform: i === current ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
                    pointerEvents: i === current ? 'auto' : 'none',
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${s.color}18` }}
                  >
                    <SlideIcon className="w-7 h-7" style={{ color: s.color }} />
                  </div>
                  <h2 className="text-2xl font-semibold font-['Space_Grotesk'] tracking-tight mb-3">
                    {t(`authSlider.${s.key}Title`)}
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-sm">
                    {t(`authSlider.${s.key}Desc`)}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Dots */}
          <div className="flex items-center gap-2 mt-4">
            {slides.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setCurrent(i)}
                className="transition-all duration-300"
                style={{
                  width: i === current ? 28 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: i === current ? slide.color : 'rgba(255,255,255,0.15)',
                }}
                data-testid={`auth-slider-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom - Trust Badges & Stats */}
        <div className="space-y-4">
          {/* Trust Badges */}
          <div className="flex items-center gap-3">
            {trustBadges.map((badge) => {
              const BadgeIcon = badge.icon;
              return (
                <div
                  key={badge.label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[hsl(var(--border))]/40 bg-white/[0.03] text-xs text-[hsl(var(--muted-foreground))]"
                >
                  <BadgeIcon className="w-3 h-3 text-[hsl(var(--primary))]" />
                  <span>{badge.label}</span>
                </div>
              );
            })}
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-6 text-xs text-[hsl(var(--muted-foreground))]">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              <span>35,000+ {t('auth.users')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-[#F7C84B]" />
              <span>$1.2M+ {t('auth.volume')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
